import HandlePaymentMethods from "@/components/handlePaymentMethods/HandlePaymentMethods";
import { createUserBilling, updateUserBilling } from "@/graphql/mutations";
import { listUserBillings } from "@/graphql/queries";
import useAttachPaymentMethod from "@/hooks/stripe/useAttachPaymentMethod";
import useCreateCustomer from "@/hooks/stripe/useCreateCustomer";
import useDetachPaymentMethod from "@/hooks/stripe/useDetachPaymentMethod";
import { useGetCustomerPaymentMethods } from "@/hooks/stripe/useGetCustomerPaymentMethods";
import { useAppSelector } from "@/store/hooks";
import { UserBilling } from "@/types/AppTypes";
import { executeGraphQLMutation } from "@/utils/helper/graphql-helper";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { v4 as uuid } from "uuid";


export const fetchUserBilling = async (userID: string): Promise<UserBilling | undefined> => {
  const userBillings = await executeGraphQLMutation({
    query: listUserBillings,
    variables: {
      filter: {
        user_id: { eq: userID },
      },
    },
  });

  return userBillings?.items?.[0];
};

const UserPaymentMethod = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
    const [newCardLoading, setNewCardLoading] = useState(false);
  const {
    getPaymentMethods,
    error: paymentMethodsError,
  } = useGetCustomerPaymentMethods();
  const paymentMethodRef = useRef<any>(null);
  const [userExistingStripePaymentMethods, setUserExistingStripePaymentMethods] = useState<any[]>(
    []
  );
  const {
    detachPaymentMethod,
    loading: detachLoading,
    error: detachedError,
  } = useDetachPaymentMethod();
  const { createCustomer, loading: customerLoading, error: customerError } = useCreateCustomer();
  const {
    attachPaymentMethod,
    loading: attachLoading,
    error: attachError,
    attachedPaymentMethod,
  } = useAttachPaymentMethod();
  const [userBilling, setUserBilling] = useState<UserBilling | undefined>(undefined);
  const handleFetchUserPaymentDetails = async (needLoading?: boolean) => {
    try {
        if (needLoading) {
        setPaymentMethodsLoading(true);
      }
      const userBilling = await fetchUserBilling(user?.user_id || "");

      if (userBilling && userBilling.customer_id) {
        setUserBilling(userBilling);
        const methods = await getPaymentMethods(userBilling?.customer_id || "");

        if (methods && methods.data) {
          setUserExistingStripePaymentMethods(methods?.data);
        }
      }
    } catch (err) {
      console.error("Failed to get payment methods:", err);
    } finally {
        if (needLoading) {
          setPaymentMethodsLoading(false);
        }
    }
  };

  useEffect(() => {
    if (user && user.user_id) {
      handleFetchUserPaymentDetails(true);
    }
  }, []);

  const handleRemoveStripePaymentMethod = async (paymentMethodId: string) => {
    try {
      toast.loading("Removing payment method...");
      const detached = await detachPaymentMethod({ paymentMethodId });
      if (!detached || detachedError) {
        toast.dismiss();
        console.error("Failed to detach payment method:", detachedError);
        throw new Error("Failed to detach payment method");
      }
    
      if (!userBilling) {
        toast.dismiss();
        console.error("User billing information is not available");
        throw new Error("User billing information is not available");
      }
      const billingResult = await executeGraphQLMutation({
        query: updateUserBilling,
        variables: {
          input: {
            id: userBilling.id,
            payment_id: userBilling.payment_id
              ? userBilling.payment_id.filter((id: string) => id !== paymentMethodId)
              : [],
            updated_at: new Date().toISOString(),
          },
        },
      });
      if (!billingResult) {
        console.error("Failed to update user billing:", billingResult);
        throw new Error("Failed to update user billing");
      }
      await handleFetchUserPaymentDetails();
      toast.success("Payment method removed successfully");
    } catch (error) {
      console.error("Error removing payment method:", error);
      throw error;
    } finally {
      toast.dismiss();
    }
  };

  const handlePaymentMethodSubmit = async () => {
    try {
        setNewCardLoading(true);
      let customerId = userBilling?.customer_id || null;

      // Step 1: Create customer if needed
      if (!customerId) {
        const customer = await createCustomer({
          name: `${user?.first_name} ${user?.last_name}`,
          email: user?.email ?? "",
        });

        if (!customer) {
          console.error("Customer creation error:", customerError);
          throw new Error("Failed to create customer");
        }
        customerId = customer.id;
      }
      // Step 2: Create payment method

      const billingDetails = {
        name: `${user?.first_name} ${user?.last_name}`,
        email: user?.email ?? "",
      };

      const paymentMethod = await paymentMethodRef.current?.createPaymentMethod(billingDetails);

      if (!paymentMethod) {
        console.error("Payment method creation error:", paymentMethod.error);
        throw new Error("Failed to create payment_method");
      }

      let paymentMethodId = paymentMethod.id;

      // Step 3: Attach payment method
      if (paymentMethodId && customerId) {
        const attachResult = await attachPaymentMethod({
          customerId: customerId,
          paymentMethodId: paymentMethodId,
        });

        // CRITICAL: Check both result and error state
        if (!attachResult || attachError) {
          console.error("Attach payment method error:", attachError);
          throw new Error("Failed to attach payment method");
        }
      }
      let billingResult = null;
      if (userBilling) {
        if (!userBilling.payment_id?.includes(paymentMethodId || "")) {
          billingResult = await executeGraphQLMutation({
            query: updateUserBilling,
            variables: {
              input: {
                id: userBilling.id,
                payment_id: [...(userBilling.payment_id || []), paymentMethodId],
                updated_at: new Date().toISOString(),
              },
            },
          });

          // CRITICAL: Check if billing update failed
          if (!billingResult) {
            throw new Error("Billing update failed");
          }
        }
      } else {
        billingResult = await executeGraphQLMutation({
          query: createUserBilling,
          variables: {
            input: {
              id: uuid(),
              user_id: user?.user_id || "",
              customer_id: customerId,
              payment_id: [paymentMethodId],
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            },
          },
        });

        // CRITICAL: Check if billing creation failed
        if (!billingResult) {
          throw new Error("Billing creation failed");
        }
      }
      await handleFetchUserPaymentDetails();
      toast.success("Payment method added successfully");
    } catch (error) {
      console.error("Error submitting payment method:", error);
    }finally{
        setNewCardLoading(false);
    }
  };


  return (
    <div className="space-y-6 w-full h-full ">
      {/* <Card className="w-full h-full">
        <CardHeader>
          <CardTitle>Payment Methods</CardTitle>
          <CardDescription>Manage your saved payment methods</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="font-semibold mb-3">Saved Cards</h3>
            <div className="space-y-3">
              {savedCards.map((card) => (
                <div
                  key={card.id}
                  className="border rounded-lg p-4 flex items-center justify-between"
                >
                  <div className="flex items-center space-x-3">
                    <CreditCard className="h-5 w-5 text-gray-500" />
                    <div>
                      <p className="font-medium">•••• •••• •••• {card.last4}</p>
                      <p className="text-sm text-gray-500">
                        Expires {card.expiryMonth}/{card.expiryYear}
                        {card.isDefault && (
                          <span className="ml-2 text-green-600 font-medium">(Default)</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    {!card.isDefault && (
                      <Button variant="outline" size="sm" onClick={() => handleSetDefault(card.id)}>
                        Set Default
                      </Button>
                    )}
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleRemoveCard(card.id)}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {!showAddForm && (
            <Button variant="outline" className="w-full" onClick={() => setShowAddForm(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add New Card
            </Button>
          )}
          {showAddForm && (
            <Card>
              <CardHeader>
                <CardTitle>Add New Payment Method</CardTitle>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField
                      control={form.control}
                      name="cardNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Card Number</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="4242 4242 4242 4242"
                              onChange={(e) => {
                                // Format card number with spaces
                                const value = e.target.value
                                  .replace(/\s/g, "")
                                  .replace(/(.{4})/g, "$1 ")
                                  .trim();
                                field.onChange(value);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="expiryDate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Expiry Date</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                placeholder="MM/YY"
                                onChange={(e) => {
                                  // Format expiry date
                                  let value = e.target.value.replace(/\D/g, "");
                                  if (value.length >= 2) {
                                    value = value.substring(0, 2) + "/" + value.substring(2, 4);
                                  }
                                  field.onChange(value);
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="cvc"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>CVC</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="123" maxLength={4} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="nameOnCard"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Name on Card</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Jane Smith" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="saveCard"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel>Save this card for future purchases</FormLabel>
                          </div>
                        </FormItem>
                      )}
                    />

                    <div className="flex gap-2 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setShowAddForm(false);
                          form.reset();
                        }}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        className="flex-1 bg-black text-white hover:bg-gray-800"
                        disabled={isLoading}
                      >
                        {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Save Card
                      </Button>
                    </div>
                  </form>
                </Form>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card> */}

<HandlePaymentMethods
        existingStripePaymentMethods={userExistingStripePaymentMethods}
        ref={paymentMethodRef}
        handlePaymentMethodSubmit={handlePaymentMethodSubmit}
        onRemoveStripePaymentMethod={handleRemoveStripePaymentMethod}
        loading={newCardLoading}
        existingStripePaymentMethodsLoading={paymentMethodsLoading}
      />
    </div>
  );
};

export default UserPaymentMethod;
